import { useCallback, useEffect, useState } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { ListNode, ListItemNode } from '@lexical/list'
import { LinkNode, AutoLinkNode } from '@lexical/link'
import { useTheme } from 'styled-components'
import RichTextToolbar from './RichTextToolbar'
import RichTextEditor from './RichTextEditor'
import LinkDialog from './LinkDialog'
import { RichTextContainer } from './FormattingStyles'

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 640

const editorConfig = {
    namespace: 'uAppRichText',
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode],
    onError(error) {
        console.error('[RichText]', error)
    }
}

export default function RichText({ name, value, setValue, placeholder, rows, isRed, resizable, testId }) {
    const [mobile, setMobile] = useState(isMobile())
    const [linkModalOpen, setLinkModalOpen] = useState(false)
    const [pendingLinkUrl, setPendingLinkUrl] = useState('')
    const [linkConfirmCallback, setLinkConfirmCallback] = useState(null)
    const theme = useTheme()

    useEffect(() => {
        const handler = () => setMobile(isMobile())
        window.addEventListener('resize', handler)
        return () => window.removeEventListener('resize', handler)
    }, [])

    const handleChange = useCallback((json) => {
        setValue({ target: { name, value: json } })
    }, [setValue, name])

    const openLinkModal = useCallback((initialUrl, onConfirm) => {
        setPendingLinkUrl(initialUrl || '')
        setLinkConfirmCallback(() => onConfirm)
        setLinkModalOpen(true)
    }, [])

    const handleLinkConfirm = (url) => {
        if (linkConfirmCallback) linkConfirmCallback(url)
        setLinkModalOpen(false)
    }

    return (
        <>
            <RichTextContainer className={isRed ? 'is-required-error' : ''} data-testid={testId}>
                <LexicalComposer initialConfig={editorConfig}>
                    <RichTextToolbar showUndoRedo={!mobile} openLinkModal={openLinkModal} />
                    <RichTextEditor
                        rows={rows}
                        placeholder={placeholder}
                        value={value}
                        onChange={handleChange}
                        resizable={resizable}
                    />
                    <HistoryPlugin />
                    <ListPlugin />
                    <LinkPlugin />
                    <ClickableLinkPlugin />
                </LexicalComposer>
            </RichTextContainer>

            <LinkDialog
                show={linkModalOpen}
                initialUrl={pendingLinkUrl}
                onClose={() => setLinkModalOpen(false)}
                onConfirm={handleLinkConfirm}
            />
        </>
    )
}
